/**
 * 가격 코리도 계산 엔진 (pricing-corridor) — WO-012 §4.4 이식.
 *
 * 기존 `src/lib/margin/model.ts`의 `offerCalc()`는 "주문량이 걸린 단일 구간 단가를
 * 전량 적용"(= 여기의 `retroactive`)이며, `model.test.ts`가 그 수치를 잠근다.
 * 그 동작·잠금값은 **건드리지 않는다.** 이 모듈은 그 위에 다음을 신규로 얹는다:
 *   - prospective(계단식 누적) — "티어 상승은 이후 주문에만 적용, 소급 없음"
 *   - retroDiff(prospective − retroactive) — 소급 요구 시 우리 손실 (50대↑ 146,000 고정)
 *   - marginPair(순/총원가 병기) · freeLeverage(무상 레버리지)
 *
 * 순수 TS (브라우저 + Node/vitest 양용). 세율/관세 수치 없음 (DP-3).
 */

export type PricingMode = "prospective" | "retroactive" | "flat";

/** 가격 티어 한 구간 (price_tier 행과 동형). */
export interface PriceTier {
  qtyFrom: number;
  qtyTo: number | null; // null = 무제한(마지막 구간)
  unitPriceUsd: number;
  labelEn?: string | null;
  labelKo?: string | null;
  labelZh?: string | null;
}

/** 계단 내역 한 줄 — 어느 구간에서 몇 대가 어떤 단가로 잡혔는지. */
export interface TierBand {
  qtyFrom: number;
  qtyTo: number | null;
  unitPriceUsd: number;
  span: number; // 이 구간에 걸린 대수
  subtotalUsd: number; // span × unitPriceUsd
  labelKo?: string | null;
}

/** tiers 를 qtyFrom 오름차순으로 정규화 (시드가 정렬돼 있어도 방어적으로). */
function sortTiers(tiers: PriceTier[]): PriceTier[] {
  return [...tiers].sort((a, b) => a.qtyFrom - b.qtyFrom);
}

/**
 * 누적 구매 수량에 따른 총액(USD).
 *   prospective — 각 구간이 계단식으로 쌓임 (티어 상승은 이후 주문에만).
 *   retroactive — 최종 도달 티어 단가를 전량에 소급 (비교용). offerCalc 과 동치.
 *   flat        — 단일 단가 × 수량 (첫 티어 단가 사용).
 */
export function calcTotal(units: number, tiers: PriceTier[], mode: PricingMode): number {
  if (units <= 0) return 0;
  const ts = sortTiers(tiers);
  if (ts.length === 0) return 0;

  if (mode === "flat") {
    return units * ts[0]!.unitPriceUsd;
  }

  if (mode === "retroactive") {
    const t = ts.find((x) => units >= x.qtyFrom && (x.qtyTo === null || units <= x.qtyTo));
    if (!t) throw new Error(`calcTotal: no tier covers ${units} units`);
    return units * t.unitPriceUsd;
  }

  // prospective — 계단식 누적
  let total = 0;
  for (const t of ts) {
    if (units < t.qtyFrom) break;
    const upper = t.qtyTo ?? units;
    const span = Math.min(units, upper) - t.qtyFrom + 1;
    if (span > 0) total += span * t.unitPriceUsd;
  }
  return total;
}

/** 블렌디드(평균) 단가 = 총액 / 수량. */
export function blendedUnit(units: number, tiers: PriceTier[], mode: PricingMode): number {
  return units > 0 ? calcTotal(units, tiers, mode) / units : 0;
}

/** prospective 계단 내역 (표시용). */
export function tierBreakdown(units: number, tiers: PriceTier[]): TierBand[] {
  if (units <= 0) return [];
  const ts = sortTiers(tiers);
  const bands: TierBand[] = [];
  for (const t of ts) {
    if (units < t.qtyFrom) break;
    const upper = t.qtyTo ?? units;
    const span = Math.min(units, upper) - t.qtyFrom + 1;
    if (span > 0) {
      bands.push({
        qtyFrom: t.qtyFrom,
        qtyTo: t.qtyTo,
        unitPriceUsd: t.unitPriceUsd,
        span,
        subtotalUsd: span * t.unitPriceUsd,
        labelKo: t.labelKo ?? null,
      });
    }
  }
  return bands;
}

/** prospective − retroactive = 상대의 소급 요구 시 우리 손실. tier 정책에서만 의미. */
export function retroDiff(units: number, tiers: PriceTier[]): number {
  if (units <= 0) return 0;
  return calcTotal(units, tiers, "prospective") - calcTotal(units, tiers, "retroactive");
}

/** flat 정책 총액 (편의 함수). */
export function flatTotal(units: number, unitPriceUsd: number): number {
  return units > 0 ? units * unitPriceUsd : 0;
}

/**
 * 무상 발생량 = floor(유상 누적 / N) × M.
 * 무상은 유상 티어 카운트에 포함하지 않는다(티어 상승 안 시킴). 원가는 별도로 발생.
 */
export function freeEarned(paidUnits: number, freePerN: number | null, freePerUnits = 1): number {
  if (!freePerN || freePerN <= 0) return 0;
  const paid = paidUnits > 0 ? paidUnits : 0;
  return Math.floor(paid / freePerN) * freePerUnits;
}

/** 순/총원가 병기 마진 (D1). costUsd = costKrw / fx, marginPct = (blended − costUsd)/blended. */
export interface MarginPair {
  costUsdNet: number;
  costUsdFull: number | null;
  marginPctNet: number;
  marginPctFull: number | null;
}

export function marginPair(
  blendedUsd: number,
  fx: number,
  costKrwNet: number,
  costKrwFull: number | null,
): MarginPair {
  const costUsdNet = fx > 0 ? costKrwNet / fx : 0;
  const costUsdFull = costKrwFull != null && fx > 0 ? costKrwFull / fx : null;
  return {
    costUsdNet,
    costUsdFull,
    marginPctNet: blendedUsd > 0 ? (blendedUsd - costUsdNet) / blendedUsd : 0,
    marginPctFull:
      costUsdFull != null && blendedUsd > 0 ? (blendedUsd - costUsdFull) / blendedUsd : null,
  };
}

/**
 * 무상 레버리지 = 무상 1대 체감가치 / 실비(costUsd).
 * WO-012 §3.6: 체감 13,000 / 실비 4,000 = 3.25배 → 가격 인하(레버리지 1.0)보다 유리.
 */
export function freeLeverage(referencePriceUsd: number, costKrwNet: number, fx: number): number {
  const costUsd = fx > 0 ? costKrwNet / fx : 0;
  return costUsd > 0 ? referencePriceUsd / costUsd : 0;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 실제 계약 모델 (2026-07-16 사용자 정정)
 * MOQ 가격 = **최종 납품 대수가 속한 구간 단가를 전량에 적용**(= retroactive).
 * 구간별 혼합 평균(prospective/blended)은 이 계약의 모델이 아니다.
 * ────────────────────────────────────────────────────────────────────────────*/

/**
 * 최종(약정) 대수가 속한 구간의 단가 = retroactive 표준 단가.
 * flat 정책([1,null] 단일 구간)에도 그대로 동작한다.
 * 예: TW 50대 → 13,000 / 45대 → 14,000 / 30대 → 15,000.
 */
export function tierUnitPrice(units: number, tiers: PriceTier[]): number {
  if (units <= 0) return 0;
  const ts = sortTiers(tiers);
  const t = ts.find((x) => units >= x.qtyFrom && (x.qtyTo === null || units <= x.qtyTo));
  return t ? t.unitPriceUsd : 0;
}

/**
 * 미달분 clawback 노출 — 약정 대수 기준으로 낮은 구간 단가를 적용해줬는데
 * 실인수가 미달하면, 실인수가 속한 구간의 정가와의 차액을 **실인수 전량**에 대해 회수해야 한다.
 *
 *   clawback = actual × (tierUnitPrice(actual) − tierUnitPrice(committed))
 *
 * 단가는 대수에 대해 비증가이므로 actual ≤ committed 일 때만 양수. actual ≥ committed → 0.
 * 예: 약정 50대(13,000) → 실인수 30대 = 30 × (15,000 − 13,000) = 60,000.
 */
export function clawbackExposure(committedUnits: number, actualUnits: number, tiers: PriceTier[]): number {
  if (committedUnits <= 0 || actualUnits <= 0) return 0;
  const diff = tierUnitPrice(actualUnits, tiers) - tierUnitPrice(committedUnits, tiers);
  return diff > 0 ? actualUnits * diff : 0;
}
